import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { Box, Checkbox, FormControl, Grid, Typography } from "@mui/material";
import { format } from "date-fns";

import { ShiftDetail } from "../orderAPI";
import { DialogBox } from "../../../common/components/functional/dialogBox/DialogBox";
import { TextFieldLight } from "../../../common/components/styled/styled";
import { AutocompleteType } from "../../../common/types/autoComplete";
import AlertWarningIcon from "../../../common/icons/alertWarningBlack.svg";

type Props = {
  isOpen: boolean;
  closeDialog: () => void;
  shifts: { id: string; version: number; details: ShiftDetail }[];
  costCenters: AutocompleteType[];
  removeShiftsFromBundle: (shiftsIds: ShiftWithVersion[]) => void;
};

type ShiftWithVersion = {
  id: string;
  version: number;
};

type ShiftProps = {
  id: string;
  version: number;
  details: ShiftDetail;
  checkboxDisabled: boolean;
};

export const DisassembleBundleDialog = ({
  isOpen,
  closeDialog,
  shifts,
  costCenters,
  removeShiftsFromBundle,
}: Props) => {
  const { t } = useTranslation("order");

  const [shiftIds, setShiftIds] = useState<ShiftWithVersion[]>([]);

  const allShiftsSelected = useMemo(() => {
    return shifts.every((shift) => shiftIds.some((el) => el.id === shift.id));
  }, [shifts, shiftIds]);

  const finishDialog = () => {
    setShiftIds([]);
    removeShiftsFromBundle(shiftIds);
  };

  const cancelDialog = () => {
    setShiftIds([]);
    closeDialog();
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    shift: ShiftWithVersion,
  ) => {
    event.target.checked
      ? setShiftIds([...shiftIds, shift])
      : setShiftIds(shiftIds.filter((el) => el.id !== shift.id));
  };

  const TitleRow = () => (
    <>
      <Grid item xs={1} />
      <Grid item xs={3} textAlign="center">
        <Typography fontSize={13}>{t("common:date")}</Typography>
      </Grid>
      <Grid item xs={3} textAlign="center">
        <Typography fontSize={13}>{t("time")}</Typography>
      </Grid>
      <Grid item xs={5} textAlign="center">
        <Typography fontSize={13}>{t("customerCostCenter")}</Typography>
      </Grid>
    </>
  );

  const ShiftRow = ({ id, version, details, checkboxDisabled }: ShiftProps) => {
    const shiftTime = `${format(
      new Date(details.time.start),
      "HH:mm",
    )} - ${format(new Date(details.time.end), "HH:mm")}`;
    return (
      <>
        <Grid item xs={1}>
          <Checkbox
            disabled={checkboxDisabled}
            checked={!!shiftIds.find((el) => el.id === id)}
            onChange={(e) => handleChange(e, { id, version })}
          />
        </Grid>
        <Grid item xs={3}>
          <FormControl fullWidth>
            <TextFieldLight
              value={format(new Date(details.time.start), "dd.MM.yyyy")}
              size="small"
              disabled
            />
          </FormControl>
        </Grid>
        <Grid item xs={3}>
          <FormControl fullWidth>
            <TextFieldLight value={shiftTime} size="small" disabled />
          </FormControl>
        </Grid>
        <Grid item xs={5}>
          <FormControl fullWidth>
            <TextFieldLight
              value={costCenters.find((cc) => cc.id === details.custCCId)?.name}
              size="small"
              disabled
            />
          </FormControl>
        </Grid>
      </>
    );
  };

  const steps = [
    {
      label: "",
      component: (
        <Box>
          <Typography sx={{ marginBottom: "15px" }}>
            {t("removeSelectedShiftsFromBundle")}
          </Typography>
          <Grid container spacing={1}>
            <TitleRow />
            {shifts.map((shift, i) => (
              <ShiftRow
                key={i}
                id={shift.id}
                version={shift.version}
                details={shift.details}
                checkboxDisabled={false}
              />
            ))}
            {allShiftsSelected && (
              <Grid
                item
                xs={12}
                sx={{
                  marginLeft: "8px",
                  marginTop: "15px",
                  padding: "8px",
                  borderRadius: "4px",
                  backgroundColor: "rgba(255, 190, 97, 0.50)",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <img
                  src={AlertWarningIcon}
                  alt={"warningIcon"}
                  style={{ marginRight: "15px", height: "24px", fill: "black" }}
                />
                <Typography>{t("removingAllShiftsDisabledCheck")}</Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      ),
      disableNext: shiftIds.length === 0 || allShiftsSelected,
    },
    {
      label: "",
      component: (
        <Box>
          <Typography sx={{ marginBottom: "15px" }}>
            {t("confirmRemoveSelectedShiftsFromBundle")}
          </Typography>
          <Grid container>
            <TitleRow />
            {shiftIds.map((data, i) => (
              <ShiftRow
                key={i}
                id={data.id}
                version={data.version}
                details={shifts.find((shift) => data.id === shift.id)!!.details}
                checkboxDisabled={true}
              />
            ))}
            <Grid
              item
              xs={12}
              sx={{
                marginTop: "15px",
                padding: "8px",
                borderRadius: "4px",
                backgroundColor: "rgba(255, 190, 97, 0.50)",
                display: "flex",
                alignItems: "center",
              }}
            >
              <img
                src={AlertWarningIcon}
                alt={"warningIcon"}
                style={{ marginRight: "15px", height: "24px", fill: "black" }}
              />
              <Typography>{t("bundleDisassembleInfo")}</Typography>
            </Grid>
          </Grid>
        </Box>
      ),
    },
  ];

  return (
    <DialogBox
      isOpen={isOpen}
      dialogTitle={t("disassembleBundle")}
      onCancelDialog={cancelDialog}
      onFinishDialog={finishDialog}
      steps={steps}
      cancelLabel={t("common:cancel")}
      finishLabel={t("common:confirm")}
    />
  );
};
